import React, {useEffect, useRef, useState} from 'react';
import {connect} from "react-redux";
import {useHistory, useLocation} from "react-router-dom";

import SidebarContent from "./components/SidebarContent";
import SidebarDesktopContainer from "./components/SidebarContent/components/SidebarDesktopContainer";
import SidebarMobileContainer from "./components/SidebarContent/components/SidebarMobileContainer";

import {OMSRoutes} from "utils/OMSRoutes";
import {MarketplaceRoutes} from "utils/MarketplaceRoutes";
import {WMS_ROOT} from "utils/WMSRoutes";
import {APPLICATIONS} from "utils/Applications";
import {UserAgentUtils} from "utils/useragent";

import {SidebarContext} from "./SidebarContext";
import MobileSidebarHeader from "./components/SidebarContent/MobileHeader";
import {Grid} from "@mui/material";
import getStripeLink from "api/retailer/stripeLink";

const headerHeight = '64px'

function NewSidebar(props) {
    const location = useLocation()
    const history = useHistory();

    const [isMobile, setIsMobile] = useState(UserAgentUtils.isMobile())
    const [currentInventory, setCurrentInventory] = useState(null);
    const [currentMember, setCurrentMember] = useState(null);
    const [currentMemberAuthorizations, setCurrentMemberAuthorizations] = useState(null);
    const [currentOrganization, setCurrentOrganization] = useState(null)

    const [memberInventories, setMemberInventories] = useState([]);
    const [memberOrganizations,  setMemberOrganizations] = useState([]);

    const [organizationApplications, setOrganizationApplications] = useState(null);
    const [selectedApplication, setSelectedApplication] = useState(APPLICATIONS.MARKETPLACE)

    const [openSidebar, setOpenSidebar] = useState(false)

    const [isBottomDrawerOpen, setIsBottomDrawerOpen] = useState(false)

    const selectedRetailerRef = useRef(props.selectedRetailer);

    const [stripeLink, setStripeLink] = useState()

    const currentMemberIdRef = useRef()

    function getStripeLinkFromMember(member) {
        if (!member || !member.retailers?.length) {
            return Promise.resolve(null);
        }

        const firstRetailerId = member.retailers[0]["@id"].substring(11);

        const stripeAccounts = member?.stripeAccounts;

        if (
            !!stripeAccounts && stripeAccounts.hasOwnProperty(firstRetailerId)
            && !!stripeAccounts[firstRetailerId]
            && stripeAccounts[firstRetailerId].hasAccount === true
        ) {
            return getStripeLink(firstRetailerId).then((retrieved) => {
                if (retrieved.success) {
                    return retrieved.url;
                }
            });
        } else {
            return Promise.resolve(null)
        }
    }

    // componentDidMount
    // it could be a good idea to put this in the src/index.js later
    useEffect(() => {
        setCurrentMember(props.member)

        // Use ref to prevent unwanted state reset when component reload.
        if (props.member?.id !== currentMemberIdRef.current && localStorage.getItem('organizationId')) {
             getStripeLinkFromMember(props.member).then((response) => {
                setStripeLink(response)
             })

            currentMemberIdRef.current = props.member.id
        }

        setCurrentMemberAuthorizations(props.authorizations)

        setCurrentInventory(props.member?.inventories?.[0])
        setMemberInventories(props.member?.inventories || []);

        setCurrentOrganization(props.organization)
        setMemberOrganizations(Object.values(props.memberOrganizations || {}))

        setOrganizationApplications(props.applications)

        if (props.selectedRetailer && selectedRetailerRef.current !== props.selectedRetailer) {
            selectedRetailerRef.current = props.selectedRetailer
        }

        setIsMobile(UserAgentUtils.isMobileOrTablet())
    },[props.member, props.authorizations, props.organization, props.memberOrganizations, props.applications, props.selectedRetailer]);

    useEffect(() => {
        const path = window.location.pathname

        if (path.startsWith(OMSRoutes.PICKING) || path.startsWith(OMSRoutes.QUOTES) || path.startsWith(OMSRoutes.QUOTE_DETAILS)) {
            setSelectedApplication(APPLICATIONS.OMS);
        } else if (path.startsWith(WMS_ROOT)) {
            setSelectedApplication(APPLICATIONS.WMS)
        } else {
            setSelectedApplication(APPLICATIONS.MARKETPLACE)
        }
    }, [window.location.pathname]);

    const handleOpenSidebar = () => {
        setOpenSidebar(true)
    }

    const handleCloseSidebar = () => {
        setOpenSidebar(false)
    }

    if ([
        MarketplaceRoutes.ERROR,
        MarketplaceRoutes.LOGOUT,
        MarketplaceRoutes.LOGIN,
        MarketplaceRoutes.RETAILER_STRIPE_CONFIRM,
        MarketplaceRoutes.AUTHENTICATION_ACCEPT_INVITATION,
        MarketplaceRoutes.INVITATION,
        MarketplaceRoutes.STRIPE_SIGNUP,
        MarketplaceRoutes.SIGNUP
    ].find((route) => window.location.pathname.startsWith(route))) {
        return null;
    }

    if (!props.member) {
        return null;
    }

    // This is the same logic as legacy code in old sidebar.
    const getRetailerFromMember = () => {
        if (currentMember?.retailers?.length > 0) {
            return currentMember.retailers[0]
        } else {
            return null;
        }
    }

    return (
        <SidebarContext.Provider value={{
            currentMemberAuthorizations,
            currentMember,
            currentInventory,
            memberInventories,
            setCurrentInventory,
            currentOrganization,
            memberOrganizations,
            isMobile: isMobile,
            organizationApplications,
            selectedApplication,
            stripeLink: stripeLink,
            headerHeight: headerHeight,
            setSelectedApplication,
            openBottomDrawer: () => setIsBottomDrawerOpen(true),
            closeBottomDrawer: () => setIsBottomDrawerOpen(false),
            currentPath: location.pathname,
            currentFullPath: location.pathname+location.search,
            isSidebarOpen: openSidebar,
            closeSidebar: () => handleCloseSidebar(),
            setOpenSidebar: () => handleOpenSidebar(),
            isUserAdmin: currentMember?.retailers === null, // legacy way to determine an if user is admin,
            selectedRetailer: selectedRetailerRef.current ?? getRetailerFromMember()
        }}>
            {!isMobile ? (
                <SidebarDesktopContainer>
                    <SidebarContent />
                </SidebarDesktopContainer>
            ) : (
                <React.Fragment>
                    <Grid
                        direction={"column"}
                        container
                        style={{
                            position: 'relative',
                            zIndex: isBottomDrawerOpen ? 1200 : 1201
                        }}
                    >
                        <MobileSidebarHeader
                            organization={currentOrganization}
                            fixedHeight = {headerHeight}
                            isBottomDrawerOpen ={isBottomDrawerOpen}
                        />
                        <SidebarMobileContainer
                            open={openSidebar}
                            onClose={handleCloseSidebar}
                            headerHeight={headerHeight}
                        >
                            <SidebarContent />
                        </SidebarMobileContainer>
                    </Grid>
                </React.Fragment>
            )}
        </SidebarContext.Provider>
    )
}

const mapStateToProps = state => {
    return {
        selectedRetailer: state.retailer.show.retrieved,
        authorizations: state.authentication.authorizations,
        applications: state.authentication.applications,
        member: state.authentication.member,
        organization: state.currentOrganization.retrieved,
        memberOrganizations: state.authentication.organizations
    };
};

export default connect(mapStateToProps)(NewSidebar);
